/**
 * Carrier o'ng tugma menyusi: elementlar (features/carriers/menu.ts) + oynalar (tahrirlash, yangi vakansiya).
 * Sahifa `actions.dialogs` ni bir marta chizadi. Status o'zgarishi versiya bilan (409 -- ziddiyat xabari).
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { callPhone, copyText, openInNewTab, sendEmail } from "@/lib/clipboard";
import { useMe } from "@/features/auth/hooks";
import { can } from "@/features/auth/types";
import { useArchiveCarrier, usePatchCarrier } from "@/features/carriers/api";
import { carrierMenu } from "@/features/carriers/menu";
import type { Carrier } from "@/features/carriers/types";
import type { MenuEntry } from "@/components/ui/context-menu";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { JobFormModal } from "@/pages/jobs/JobFormModal";
import { CarrierFormModal } from "./CarrierFormModal";

export function useActionFailure() {
  const { t } = useTranslation("menu");
  return useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.isConflict) toast.warning(t("conflict"));
      else toast.error(err instanceof ApiError ? err.message : t("failed"));
    },
    [t],
  );
}

export function useCarrierActions() {
  const me = useMe().data;
  const navigate = useNavigate();
  const { t } = useTranslation("menu");
  const { t: tc } = useTranslation();
  const patch = usePatchCarrier();
  const archive = useArchiveCarrier();
  const fail = useActionFailure();
  const [dialog, setDialog] = useState<{ kind: "edit" | "new-job"; carrier: Carrier } | null>(null);

  const items = useCallback(
    (c: Carrier): MenuEntry[] => {
      if (!me) return [];
      const path = `/carriers/${c.id}`;
      return carrierMenu(
        c,
        can(me, "carriers_edit"),
        {
          open: () => navigate(path),
          openNewTab: () => openInNewTab(path),
          navigate: (p) => navigate(p),
          copy: (text) => void copyText(text),
          call: callPhone,
          email: sendEmail,
          newJob: () => setDialog({ kind: "new-job", carrier: c }),
          edit: () => setDialog({ kind: "edit", carrier: c }),
          setStatus: (status) =>
            patch.mutate(
              { id: c.id, status, version: c.version },
              { onSuccess: () => toast.success(t("statusChanged", { name: c.name, status: tc(`status.${status}`) })), onError: fail },
            ),
          archive: () =>
            void confirmDialog({ title: t("archiveTitle", { name: c.name }), description: t("carrier.archiveDesc"), confirmLabel: t("archive"), danger: true }).then(
              (ok) => ok && archive.mutate(c.id, { onSuccess: () => toast.success(t("archivedToast", { name: c.name })), onError: fail }),
            ),
        },
        t,
        tc,
      );
    },
    [me, navigate, patch, archive, fail, t, tc],
  );

  const close = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const dialogs = (
    <>
      {dialog?.kind === "edit" && <CarrierFormModal open onOpenChange={close} carrier={dialog.carrier} />}
      {dialog?.kind === "new-job" && <JobFormModal open onOpenChange={close} carrierId={dialog.carrier.id} onSaved={(j) => navigate(`/jobs/${j.id}`)} />}
    </>
  );
  return { items, dialogs };
}
